import { useQuery } from '@apollo/client';
import { StarBorderOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { SingleColumnFormLayout } from '@procureai/ui-form-mui';
import { FormWidgetI18nConfig, SimpleFormWidget, SimpleFormWidgetConfigProps } from '@procureai/ui-modules-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '@/legacy/common/types';
import { useModuleContext } from '@/legacy/components/ModuleContextProvider';

import { useAwardingConfigurationWidgetBackend } from './AwardingConfigurationWidget.hooks';
import { AWARDING_CONFIGURATION_WIDGET_QUERY } from './AwardingConfigurationWidget.query';
import { AwardingConfigurationFormData, awardingConfigurationFormSchema } from './AwardingConfigurationWidget.schema';
import { AwardingConfigurationForm } from './components/AwardingConfigurationForm/AwardingConfigurationForm';

const config: SimpleFormWidgetConfigProps<AwardingConfigurationFormData> = {
  schema: awardingConfigurationFormSchema,
  defaultValues: {},
  iconComponent: StarBorderOutlined,
};

const i18n: FormWidgetI18nConfig = {
  ns: I18nNamespace.widgets,
  keyPrefix: 'AwardingConfigurationWidget',
};

export const AwardingConfigurationWidget = () => {
  const { entityId } = useModuleContext();
  const variables = { id: entityId };
  const { data } = useQuery(AWARDING_CONFIGURATION_WIDGET_QUERY, { variables });
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'AwardingConfigurationWidget' });

  return (
    <SimpleFormWidget<AwardingConfigurationFormData>
      {...config}
      i18n={i18n}
      useBackend={useAwardingConfigurationWidgetBackend}
    >
      {(props) => {
        const deadline = data?.tesseract.demand.awardingConfiguration.deadline;

        return (
          <SingleColumnFormLayout>
            <Stack spacing={4}>
              {deadline && (
                <Alert severity="info">
                  <AlertTitle>{t('deadlineSet.title')}</AlertTitle>
                  <Typography variant="body2">
                    {t('deadlineSet.text', {
                      deadline,
                    })}
                  </Typography>
                </Alert>
              )}
              <AwardingConfigurationForm viewOnly={props.viewOnly} />
            </Stack>
          </SingleColumnFormLayout>
        );
      }}
    </SimpleFormWidget>
  );
};
